import Box from '@mui/material/Box';
import MuiLink from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { RelatedJobsPanelProps } from './RelatedJobsPanel.props';
import { Panel } from '../../../../../components/atoms/Panel';
import { StatusBadge } from '../../../../../components/atoms/StatusBadge';

const cellSx = { fontSize: '12px', py: 0.75, px: 1.5 };
const headerSx = {
  ...cellSx,
  fontWeight: 600,
  color: 'text.secondary',
  fontSize: '11px',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.03em',
};

const formatDate = (dateStr: unknown): string => {
  if (!dateStr || typeof dateStr !== 'string') return '-';
  try {
    return new Date(dateStr).toLocaleDateString(undefined, { dateStyle: 'medium' });
  } catch {
    return '-';
  }
};

const JobTable: React.FC<{ jobs: Array<Record<string, unknown>> }> = ({ jobs }) => (
  <TableContainer>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={headerSx}>Job ID</TableCell>
          <TableCell sx={headerSx}>Status</TableCell>
          <TableCell sx={headerSx}>Type</TableCell>
          <TableCell sx={headerSx}>Created</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {jobs.map((job, idx) => (
          <TableRow
            key={String(job.id ?? idx)}
            sx={{
              bgcolor: idx % 2 === 0 ? 'transparent' : 'v4.tableEven',
              '&:hover': { bgcolor: 'v4.tableHover' },
            }}
          >
            <TableCell sx={cellSx}>
              <MuiLink
                component={RouterLink}
                to={`/services/${String(job.id ?? '')}`}
                sx={{ fontSize: '12px', fontWeight: 500 }}
                underline="hover"
              >
                {String(job.id ?? '-')}
              </MuiLink>
            </TableCell>
            <TableCell sx={cellSx}>
              <StatusBadge
                status={String(job.serviceStatusName ?? job.status ?? '-')}
                size="small"
              />
            </TableCell>
            <TableCell sx={cellSx}>{String(job.type ?? job.serviceType ?? '-')}</TableCell>
            <TableCell sx={cellSx}>{formatDate(job.createdAt)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const EmptyMessage: React.FC<{ text: string }> = ({ text }) => (
  <Typography sx={{ fontSize: '13px', color: 'text.secondary', py: 1.5, textAlign: 'center' }}>
    {text}
  </Typography>
);

export const RelatedJobsPanel: React.FC<RelatedJobsPanelProps> = ({
  title = 'Related Jobs',
  actions,
  currentServiceId,
  otherJobsAtLocation = [],
  linkedJobs,
}) => {
  const showLinked = linkedJobs !== undefined;
  const filteredOther = currentServiceId
    ? otherJobsAtLocation.filter((j) => Number(j.id ?? j.serviceId) !== currentServiceId)
    : otherJobsAtLocation;

  return (
    <Panel title={title} actions={actions}>
      <Box>
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 600,
            textTransform: 'uppercase',
            color: 'text.secondary',
            letterSpacing: '0.04em',
            px: 1.5,
            pt: 1,
            pb: 0.5,
          }}
        >
          {showLinked ? 'Other Jobs at This Location' : 'Closed Jobs at This Location'}
        </Typography>
        {filteredOther.length > 0 ? (
          <JobTable jobs={filteredOther} />
        ) : (
          <EmptyMessage
            text={showLinked ? 'No other jobs at this location' : 'No closed jobs at this location'}
          />
        )}

        {showLinked && (
          <Box sx={{ borderTop: '1px solid', borderColor: 'v4.border', mt: 1 }}>
            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 600,
                textTransform: 'uppercase',
                color: 'text.secondary',
                letterSpacing: '0.04em',
                px: 1.5,
                pt: 1,
                pb: 0.5,
              }}
            >
              Linked Jobs
            </Typography>
            {linkedJobs.length > 0 ? (
              <JobTable jobs={linkedJobs} />
            ) : (
              <EmptyMessage text="No linked jobs" />
            )}
          </Box>
        )}
      </Box>
    </Panel>
  );
};
